import React, { useState, useEffect } from 'react';
import translate from "translate";
import styled from 'styled-components';

interface Product {
  firstVariantId: string;
  name: string;
  price: string;
  image: string;
}

const AddonList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-bottom: 150px;
`;

const AddonItem = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  background: linear-gradient(180deg, #e5e5e5 0%, #e5e5e5 13.7%, #eff0f0 59.6%, #f7f7f7 100%);
  padding: 12px;
  border-radius: 0;
  min-height: 145px;
  width: 100%;
  gap: 12px;
  position: relative;

  @media (max-width: 600px) {
    flex-direction: row;
    align-items: flex-start;
    min-height: 115px;
  }
`;

const AddonImage = styled.img`
  width: 50px;
  height: 50px;
  border-radius: 5px;
  object-fit: cover;
  filter: drop-shadow(20px 24px 14px rgba(0,0,0,0.18));

  @media (max-width: 600px) {
    width: 60px;
    height: 60px;
  }
`;

const AddonDetails = styled.div`
  flex-grow: 1;
  margin-left: 10px;
  width: 10px;
`;

const AddonTitle = styled.h5`
  font-family: 'Instrument Sans', sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 20px;
  margin: 0;
`;

const AddonPrice = styled.span`
  font-family: 'Instrument Sans', sans-serif;
  font-size: 12px;
  line-height: 20px;
  font-weight: bold;
  color: #191A1F;
`;

const AddButton = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: #1a1a1a;
  color: #e5e5e5;
  font-size: 30px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease-in-out;

  &:hover {
    background-color: #333;
  }

  @media (max-width: 600px) {
    position: relative;
    top: 29px;
  }

  
`;

const SuccessMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #14816E;
  font-family: 'Instrument Sans', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;

  .checkmark-container {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background-color: #14816E;
  }

  .checkmark-container svg {
    width: 8px;
    height: 6px;
    fill: white;
  }
`;


const ErrorMessageContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px;
  background: #FCE8E8;
  color: #191A1F;
  font-family: 'Instrument Sans', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  width: 100%;
  order: 1;
`;

const Subtitle = styled.h4`
  font-family: "Futura";
  font-weight: 500;
  font-size: 24px;
  line-height: 16px;
  margin: 32px 0;
`;
interface AddonComponentProps {
  profile: string;
  subtitle: string;
}


const AddonComponent: React.FC<AddonComponentProps> = ({ profile, subtitle }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [addonStatus, setAddonStatus] = useState<{ [key: string]: "success" | "error" | "idle" }>({});
  const [translatedTitle, setTranslatedTitle] = useState<string>('');
  const [translatedFailedToAdd, setTranslatedFailedToAdd] = useState<string>('');
  const [currencySymbol, setCurrencySymbol] = useState<string>('$');
  const [translatedSubTitle, setTranslatedSubTitle] = useState<string>('');
 
  


  async function addProduct(variantID: string) {
    setAddonStatus(prev => ({ ...prev, [variantID]: "idle" }));
  
    console.log(variantID);
    console.log('product added');
  
    try {
      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: [{ id: variantID, quantity: 1 }],
        }),
      });
      console.log('trying to add product');
  
      if (!response.ok) {
        console.log("Failed to add product:", response);
        throw new Error("Failed to add product");
      }
  
      const data = await response.json();
      console.log("Product added:", data);
  
      setAddonStatus(prev => ({ ...prev, [variantID]: "success" }));
  
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'product-add',
        cartData: data,
        variantId: variantID,
      });
    } catch (error) {
      console.error("Error adding product:", error);
      setAddonStatus(prev => ({ ...prev, [variantID]: "error" }));
    }
  }




  async function fetchProducts() {
    try {
      const options: RequestInit = {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      };
      const searchSpringElement = document.getElementById('react-searchspring-config');
      const searchSpringID = searchSpringElement ? searchSpringElement.getAttribute('data-site-id') : '';

      const url = `https://${searchSpringID}.a.searchspring.io/boost/zcbtly/recommend?tags=test-${profile}`;
      const response = await fetch(url, options);

      if (!response.ok) {
        console.log('Failed to fetch products:', response);
        throw new Error(`Failed to fetch products: ${response.status}`);
      }

      const data = await response.json();
      console.log('Raw data from Searchspring:', data);

      const results = data[0].results;
      console.log('Results:', results);

      const topThreeProducts = results.slice(0, 3);

      const addons = topThreeProducts.map((product: any) => {
        const { attributes } = product;

        const [firstVariantId] = attributes.variant_id.split('|') || [''];

        const [firstVariantPrice] = attributes.variant_price.split('|') || [''];

        let firstImageUrl = '';
        let rawMedia = attributes.variant_mfield_custom_variant_media || '';

        const [firstMediaBlock = ''] = rawMedia.split('|') || [];

        try {
          const parsed = JSON.parse(firstMediaBlock.trim());
          firstImageUrl = parsed?.variantMedia?.images?.[0]?.url || '';
        } catch (err) {
          console.error('Error parsing variant_mfield_custom_variant_media:', err);
        }

        return {
          name: product.mappings.core.name,
          firstVariantId,
          price: firstVariantPrice,
          image: firstImageUrl,
        };
      });

      setProducts(addons);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  }


  useEffect(() => {
    async function fetchTranslation() {
      const dataElement = document.getElementById('spectrum-sku');
      const language = dataElement?.getAttribute('data-language') || 'en';
      const titleTranslation = await translate("Added to your Cart", language);
      setTranslatedTitle(titleTranslation);
      const subtitleTranslation = await translate(subtitle, language);
      setTranslatedSubTitle(subtitleTranslation);
      const failedTranslation = await translate("Failed to add to cart. Try again.", language);
      setTranslatedFailedToAdd(failedTranslation);
      setCurrencySymbol(document.getElementById('shop-currency-symbol')?.getAttribute('data-currency-symbol') || '$');
    }
      fetchTranslation();
    }, []);

  useEffect(() => {
    fetchProducts();

  }, []);

  const [cartUpdateKey, setCartUpdateKey] = useState(0);




  return (
    <>
    <Subtitle>{translatedSubTitle}</Subtitle>
    <AddonList>
    {products.map((addon) => (
      <AddonItem key={addon.firstVariantId}>
        <AddonImage src={addon.image} />
        <AddonDetails>
          {addonStatus[addon.firstVariantId] === "success" && (
            <SuccessMessage>
              <div className="checkmark-container">
                <svg xmlns="http://www.w3.org/2000/svg" width="8" height="6" viewBox="0 0 8 6" fill="none">
                  <path d="M7.84372 0.487521C7.94379 0.587619 8 0.723363 8 0.864902C8 1.00644 7.94379 1.14219 7.84372 1.24228L3.57349 5.51251C3.47339 5.61258 3.33765 5.66879 3.19611 5.66879C3.05457 5.66879 2.91883 5.61258 2.81873 5.51251L0.149838 2.84362C0.0526058 2.74295 -0.00119599 2.60811 2.01782e-05 2.46816C0.00123635 2.3282 0.0573732 2.19433 0.15634 2.09536C0.255307 1.99639 0.389185 1.94025 0.52914 1.93904C0.669095 1.93782 0.803928 1.99162 0.9046 2.08886L3.19611 4.38037L7.08895 0.487521C7.18905 0.387453 7.3248 0.331238 7.46634 0.331238C7.60787 0.331238 7.74362 0.387453 7.84372 0.487521Z" fill="white"/>
                </svg>
              </div>
              {translatedTitle}
            </SuccessMessage>
          )}
          <AddonTitle>{addon.name}</AddonTitle>
          <AddonPrice>{currencySymbol + addon.price}</AddonPrice>
        </AddonDetails>
  
  {(!addonStatus[addon.firstVariantId] || addonStatus[addon.firstVariantId] === "idle") && (
    <AddButton onClick={() => addProduct(addon.firstVariantId)}>+</AddButton>
  )} 
  
  
        {addonStatus[addon.firstVariantId] === "error" && (
          <ErrorMessageContainer>
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
              <circle cx="9" cy="9" r="8" stroke="#DA352A"/>
              <rect y="0.347664" width="9.63435" height="0.492266" rx="0.246133" transform="matrix(0.707961 0.706252 -0.707961 0.706252 5.9434 5.10213)" fill="#D52B15" stroke="#DA352A" stroke-width="0.492266"/>
              <rect x="0.348505" width="9.63435" height="0.492266" rx="0.246133" transform="matrix(0.707961 -0.706252 0.707961 0.706252 5.23557 12.5508)" fill="#D52B15" stroke="#DA352A" stroke-width="0.492266"/>
            </svg>
            {translatedFailedToAdd}
          </ErrorMessageContainer>
        )}
      </AddonItem>
    ))}
  </AddonList>
  </>
  );
};

export default AddonComponent;
